import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import ReactGA from 'react-ga';

import { T, defineMessages } from '@sm/intl';
import {
  BasePage,
  StaticContext,
  ErrorCard,
  DesktopScreen,
  MobileScreen,
  FourOhFourError
} from '@sm/webassets';
import {
  Grid,
  Row,
  Col,
  Typography,
  Button,
  Card,
  Tabs,
  Tab
} from '@sm/wds-react';
import {
  IconTag,
  IconEmail,
  IconLock,
  IconNews,
  IconCheck,
  IconGear,
  IconUserFilled
} from '@sm/wds-icons';
import { sanitizeString, getClientEnvironmentDetails } from '@sm/utils';
import {
  MetricsTracker,
  generateMetricsAttribute,
  USER_EVENTS
} from '@sm/metrics';

import AppDirNavHeader from '../../components/AppDirNavHeader';
import AppDirectoryAppDetailsSkeleton from './AppDirectoryAppDetailsSkeleton';

import './app-directory-app-details.scss';
import publishedApplicationListingDetails from './app-directory-app-details.graphql';
import RemoveApp from '../../components/RemoveApp';
import AppPlaceHolderImg from '../../static/images/app-placeholder.png';

const COPY = defineMessages({
  APP_DETAILS_HEADER: {
    id: 'Details.AppDetails',
    defaultMessage: 'App Details',
    description:
      '[Type: Header][Vis.: low] - A header preceding the full description of the current app'
  },
  REQUIRED_ACCESS_LABEL: {
    id: 'Details.RequiredAccess',
    defaultMessage: 'This app will require access to:',
    description:
      '[Type: Label][Vis.: low] - A label preceding a list of requirements before the current application could be installed'
  },
  REQUIRED_ACCOUNT_LABEL: {
    id: 'Details.RequiredAccount',
    defaultMessage:
      'This app will require an additional {accountType} account:',
    description:
      '[Type: Label][Vis.: low] - A label preceding a description of an external account required by the app'
  },
  INSTALLED_LABEL: {
    id: 'Details.Installed',
    defaultMessage: 'Installed',
    description:
      '[Type: Label][Vis.: low] - A label indicating that the user has the current visible application installed'
  },
  INSTALL_LABEL: {
    id: 'Details.Install',
    defaultMessage: 'Install',
    description:
      '[Type: Label][Vis.: low] - A link/button that will redirect the user to the install page for the current app'
  },
  VISIT_SITE_LABEL: {
    id: 'Details.VisitSite',
    defaultMessage: 'Visit Site',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the site of the application they are trying to install'
  },
  LAUNCH_LABEL: {
    id: 'Details.Launch',
    defaultMessage: 'Launch',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the current visible application'
  },
  UPGRADE_LABEL: {
    id: 'Details.Upgrade',
    defaultMessage: 'Upgrade',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the upgrade page for the visible application'
  },
  REMOVE_APP: {
    id: 'Details.RemoveApp',
    defaultMessage: 'Remove App',
    description:
      '[Type: Link][Vis.: low] - A link/button that will open a dialog requesting the user to confirm the removal of the current app'
  }
});

const appDetailsItem = (icon, label, link, linkLabel, search) => (
  <div className="wds-flex wds-m-b-3">
    <div className="wds-m-r-3 wds-type wds-type--light-muted">{icon}</div>
    <div className="sm-app-directory-app-details__details-link-wrapper">
      <Typography>{`${label}:`}</Typography>
      {/* The query params need to be preserved between urls (needed for ut_source) */}
      <a href={`${link}${search}`}>
        <Typography
          variant="link"
          className="sm-app-directory-app-details__details-link"
        >
          {linkLabel}
        </Typography>
      </a>
    </div>
  </div>
);

const MediaCarrousel = ({ media }) => {
  const [current, setCurrent] = useState(0);
  const currentMedia = media[current];
  const { userAgent } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);

  if (!currentMedia) {
    return null;
  }

  return (
    <>
      <MobileScreen defaultMatches={!isDesktop}>
        <div className="sm-app-directory-app-details__media-container sm-app-directory-app-details__media-container--mobile">
          {media.map(m => (
            <Choose>
              <When condition={m.type === 'youtube'}>
                <div
                  key={m.url}
                  className="sm-app-directory-app-details__media-video-container sm-app-directory-app-details__media-video-container--mobile"
                >
                  <iframe
                    className="sm-app-directory-app-details__media-video-container-frame"
                    data-testid="MediaCarrousel_SelectedVideo"
                    allowFullScreen
                    src={m.url}
                    title={m.altText}
                  />
                </div>
              </When>
              <Otherwise>
                <div
                  key={m.url}
                  className="sm-app-directory-app-details__media-image-container sm-app-directory-app-details__media-image-container--mobile"
                >
                  <img
                    alt={m.altText}
                    data-testid="MediaCarrousel_SelectedImage"
                    className="sm-app-directory-app-details__media-image-container-image"
                    src={m.url}
                  />
                </div>
              </Otherwise>
            </Choose>
          ))}
        </div>
      </MobileScreen>
      <DesktopScreen defaultMatches={isDesktop}>
        <>
          <div className="sm-app-directory-app-details__media-container">
            <Choose>
              <When condition={currentMedia.type === 'youtube'}>
                <div className="sm-app-directory-app-details__media-video-container">
                  <iframe
                    className="sm-app-directory-app-details__media-video-container-frame"
                    data-testid="MediaCarrousel_SelectedVideo"
                    allowFullScreen
                    src={currentMedia.url}
                    title={currentMedia.altText}
                  />
                </div>
              </When>
              <Otherwise>
                <div className="sm-app-directory-app-details__media-image-container">
                  <img
                    alt={currentMedia.altText}
                    data-testid="MediaCarrousel_SelectedImage"
                    className="sm-app-directory-app-details__media-image-container-image"
                    src={currentMedia.url}
                  />
                </div>
              </Otherwise>
            </Choose>
          </div>
          <div
            className="sm-app-directory-app-details__media-thumbnails wds-flex"
            data-testid="MediaCarrousel_Thumbnails"
          >
            {media.map((m, mediaIdx) => (
              <div
                key={m.url}
                className={`sm-app-directory-app-details__media-thumbnails-preview ${
                  mediaIdx === current
                    ? 'sm-app-directory-app-details__media-thumbnails-preview--active'
                    : ''
                }`}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    setCurrent(mediaIdx);
                  }
                }}
                role="button"
                onClick={() => {
                  setCurrent(mediaIdx);
                }}
                tabIndex={0}
              >
                <Choose>
                  <When condition={m.type === 'youtube'}>
                    <iframe
                      data-testid="MediaCarrousel_Thumbnail"
                      className="sm-app-directory-app-details__media-thumbnails-frame"
                      allowFullScreen
                      src={m.url}
                      title={m.altText}
                    />
                  </When>
                  <Otherwise>
                    <img
                      data-testid="MediaCarrousel_Thumbnail"
                      alt={m.altText}
                      className="sm-app-directory-app-details__media-thumbnails-image"
                      src={m.url}
                    />
                  </Otherwise>
                </Choose>
              </div>
            ))}
          </div>
        </>
      </DesktopScreen>
    </>
  );
};

MediaCarrousel.propTypes = {
  media: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['youtube', 'image']).isRequired,
      url: PropTypes.string.isRequired,
      altText: PropTypes.string.isRequired
    })
  ).isRequired
};

const AppDirectoryAppDetails = ({
  match: {
    params: { appId }
  },
  location: { search, pathname }
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [pageTracked, setPageTracked] = useState(false);
  const [googleTracked, setGoogleTracked] = useState(false);
  const { userAgent } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);
  const {
    environment: { languageId }
  } = useContext(StaticContext);
  return (
    <BasePage
      pageId="App Directory - Details"
      includeHeader={false}
      legacyWeb="papi"
    >
      <AppDirNavHeader />
      <Query
        query={publishedApplicationListingDetails}
        variables={{ language: languageId, appId }}
      >
        {({
          loading,
          error,
          refetch,
          data: { publishedApplicationListingDetails: appDetails } = {}
        }) => {
          if (
            appDetails &&
            appDetails.details.googleAnalyticsTrackingId &&
            !googleTracked
          ) {
            const trackerName = 'app-dir-details-pageview';
            ReactGA.initialize(appDetails.details.googleAnalyticsTrackingId, {
              name: trackerName // this is tracker name in case there are other trackers on the page
            });
            ReactGA.pageview(pathname, [trackerName]);
            setGoogleTracked(true);
          }

          if (appDetails && !pageTracked) {
            MetricsTracker.track({
              name: USER_EVENTS.COMPONENT_ADD,
              data: {
                actionFlow: 'browsing',
                actionType: 'app_details.page_view',
                appId: appDetails.id,
                appName: appDetails.name
              }
            });
            setPageTracked(true);
          }

          return (
            <Choose>
              <When condition={error}>
                <ErrorCard data-testid="AppDirectoryAppDetails_Error" />
              </When>
              <When condition={loading}>
                <AppDirectoryAppDetailsSkeleton data-testid="AppDirectoryAppDetails_Skeleton" />
              </When>
              <When condition={!appDetails}>
                <div data-testid="AppDirectoryAppDetails_NotFound">
                  <FourOhFourError />
                </div>
              </When>
              <Otherwise>
                <Grid className="wds-m-t-6 sm-app-directory-app-details">
                  <Row>
                    <Col md={1} xs={12}>
                      <img
                        className="sm-app-directory-app-details__logo"
                        src={
                          appDetails.logo ? appDetails.logo : AppPlaceHolderImg
                        }
                        alt={appDetails.name}
                      />
                    </Col>
                    <MobileScreen defaultMatches={!isDesktop}>
                      <Col
                        xs={12}
                        className="wds-flex wds-flex--y wds-flex--x-center wds-flex--y-center wds-m-b-5"
                      >
                        <Typography variant="pageTitle" className="wds-m-b-4">
                          {appDetails.name}
                        </Typography>
                        <If condition={appDetails.installed}>
                          <Button color="info" size="sm" className="wds-m-b-4">
                            <T desc={COPY.INSTALLED_LABEL} />
                          </Button>
                        </If>
                        <Typography color="dark-muted">
                          {appDetails.tagline}
                        </Typography>
                      </Col>
                    </MobileScreen>
                    <DesktopScreen defaultMatches={isDesktop}>
                      <Col md={7}>
                        <div className="wds-flex wds-flex--y-center">
                          <Typography variant="pageTitle" className="wds-m-r-3">
                            {appDetails.name}
                          </Typography>
                          <If condition={appDetails.installed}>
                            <Button color="info" size="sm">
                              <T desc={COPY.INSTALLED_LABEL} />
                            </Button>
                          </If>
                        </div>
                        <Typography color="dark-muted">
                          {appDetails.tagline}
                        </Typography>
                      </Col>
                    </DesktopScreen>
                    <Col md={4} xs={12}>
                      <Choose>
                        <When
                          condition={
                            appDetails.details.linkType === 'VISIT_SITE'
                          }
                        >
                          <Button
                            href={`${appDetails.details.link}${search}`}
                            color="primary"
                            stretched
                          >
                            <T desc={COPY.VISIT_SITE_LABEL} />
                          </Button>
                        </When>
                        <When
                          condition={appDetails.details.linkType === 'LAUNCH'}
                        >
                          <Button
                            href={`${appDetails.details.link}${search}`}
                            color="primary"
                            stretched
                          >
                            <T desc={COPY.LAUNCH_LABEL} />
                          </Button>
                        </When>
                        <When
                          condition={appDetails.details.linkType === 'UPGRADE'}
                        >
                          <Button
                            href={`${appDetails.details.link}${search}`}
                            color="upgrade"
                            stretched
                          >
                            <T desc={COPY.UPGRADE_LABEL} />
                          </Button>
                        </When>
                        <Otherwise>
                          <Button
                            href={`${appDetails.details.link}${search}`}
                            color="primary"
                            stretched
                            data-sm-metrics={generateMetricsAttribute({
                              data: {
                                actionType: 'app_details.install_click',
                                actionFlow: 'browsing'
                              }
                            })}
                          >
                            <T desc={COPY.INSTALL_LABEL} />
                          </Button>
                        </Otherwise>
                      </Choose>
                    </Col>
                  </Row>
                  <Row className="wds-m-t-3">
                    <Col md={4} xs={12} mdOrder={1} className="wds-m-b-4">
                      <Card
                        noRule
                        title={
                          <Typography variant="cardTitle">
                            <T desc={COPY.APP_DETAILS_HEADER} />
                          </Typography>
                        }
                      >
                        {appDetailsItem(
                          <IconTag />,
                          'Publisher',
                          'https://www.surveymonkey.com',
                          appDetails.details.publisher,
                          search
                        )}
                        <If condition={appDetails.details.supportEmail}>
                          {appDetailsItem(
                            <IconEmail />,
                            'Email',
                            `mailto:${appDetails.details.supportEmail}`,
                            appDetails.details.supportEmail,
                            search
                          )}
                        </If>
                        {appDetailsItem(
                          <IconLock />,
                          'Privacy Policy',
                          appDetails.details.privacyPolicyUrl,
                          appDetails.details.privacyPolicyUrl,
                          search
                        )}
                        {appDetailsItem(
                          <IconNews />,
                          'Terms of Service',
                          appDetails.details.termsOfUseUrl,
                          appDetails.details.termsOfUseUrl,
                          search
                        )}
                      </Card>
                      <If
                        condition={
                          appDetails.installed && !appDetails.isIntegration
                        }
                      >
                        <RemoveApp
                          appId={appDetails.id}
                          onSuccess={() => refetch()}
                        >
                          <Button
                            stretched
                            color="muted"
                            className="wds-m-t-3 sm-app-directory-app-details__remove-app-button"
                          >
                            <T desc={COPY.REMOVE_APP} />
                          </Button>
                        </RemoveApp>
                      </If>
                    </Col>
                    <Col md={8} xs={12}>
                      <MediaCarrousel
                        data-testid="AppDirectoryAppDetails_MediaCarrousel"
                        media={[].concat(
                          appDetails.details.youtubeUrl
                            ? [
                                {
                                  url: appDetails.details.youtubeUrl,
                                  type: 'youtube',
                                  altText: appDetails.name
                                }
                              ]
                            : [],
                          appDetails.details.screenshots.map(s => ({
                            url: s,
                            type: 'image',
                            altText: appDetails.name
                          }))
                        )}
                      />
                      <div className="wds-m-t-3">
                        {appDetails.details.featureList.map(feature => (
                          <div
                            className="wds-flex wds-flex--y-center"
                            key={feature}
                          >
                            <IconCheck color="dark-muted" />
                            <Typography
                              className="wds-m-l-3"
                              variant="sectionTitle"
                            >
                              {feature}
                            </Typography>
                          </div>
                        ))}
                      </div>
                      <div className="wds-m-t-3 wds-m-b-3">
                        <Tabs
                          color="transparent"
                          onChange={value => setSelectedTab(value)}
                        >
                          <Tab selected={selectedTab === 0} title="Description">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sanitizeString(
                                  appDetails.fullDescription
                                )
                              }}
                            />
                          </Tab>
                          <Tab
                            selected={selectedTab === 1}
                            title="Requirements"
                            data-testid="AppDirectoryAppDetails_RequirementsTab"
                          >
                            <If condition={appDetails.details.requirements}>
                              <Typography
                                variant="cardTitle"
                                component="h3"
                                data-testid="AppDirectoryAppDetails_Requirements"
                              >
                                <T
                                  desc={COPY.REQUIRED_ACCOUNT_LABEL}
                                  values={{
                                    accountType:
                                      appDetails.details.requirements.type
                                  }}
                                />
                              </Typography>
                              <ul className="sm-app-directory-app-details__requirements wds-p-l-3 wds-p-t-3">
                                <li className="wds-flex wds-flex--y-center wds-m-b-2">
                                  <IconUserFilled color="light-muted" />
                                  <a
                                    href={
                                      appDetails.details.requirements
                                        .pricingUrl + search
                                    }
                                    target="_blank"
                                    rel="noreferrer noopener"
                                  >
                                    <Typography
                                      className="wds-m-l-2"
                                      variant="link"
                                    >
                                      {appDetails.details.requirements.label}
                                    </Typography>
                                  </a>
                                </li>
                              </ul>
                              <If
                                condition={
                                  appDetails.details.requirements.description
                                }
                              >
                                <p>
                                  {appDetails.details.requirements.description}
                                </p>
                              </If>
                            </If>
                            <If condition={appDetails.details.scopes}>
                              <Typography variant="cardTitle" component="h3">
                                <T desc={COPY.REQUIRED_ACCESS_LABEL} />
                              </Typography>
                              <ul className="sm-app-directory-app-details__requirements wds-p-l-3 wds-p-t-3">
                                {appDetails.details.scopes.map(scope => (
                                  <li
                                    className="wds-flex wds-flex--y-center wds-m-b-2"
                                    key={scope}
                                  >
                                    <IconGear color="light-muted" />
                                    <span className="wds-m-l-2">{scope}</span>
                                  </li>
                                ))}
                              </ul>
                            </If>
                          </Tab>
                        </Tabs>
                      </div>
                    </Col>
                  </Row>
                </Grid>
              </Otherwise>
            </Choose>
          );
        }}
      </Query>
    </BasePage>
  );
};

AppDirectoryAppDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      appId: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string.isRequired
  }).isRequired
};

export default AppDirectoryAppDetails;
